import {ROUTES} from 'src/constants/Routes';

import {LandingRegistryEntry} from './types';

/** Single source of truth: one entry here plus a folder under `catalog/`. */
export const LANDINGS: LandingRegistryEntry[] = [
  {slug: 'vidrala', load: () => import('./catalog/vidrala')},
  // Vidrala is the only partner on the new system for now. The rest keep their
  // `catalog/` folder and come back one line at a time; mapfre and thoughtworks
  // are not ready anyway, see the note at the top of each.
];

export const LANDING_SLUGS = LANDINGS.map(({slug}) => slug);

export const landingPath = (slug: string) => `/${slug}`;

/** Lowercase, digits and single dashes: what we are willing to put in a URL. */
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const EXTRA_RESERVED_SEGMENTS = ['embed'];

export const getAppRootSegments = (): Set<string> => {
  const segments = Object.values(ROUTES)
    .map((route) => route.split('/')[1] ?? '')
    .filter((segment) => segment !== '' && !segment.startsWith(':'))
    .map((segment) => segment.toLowerCase());

  return new Set([...segments, ...EXTRA_RESERVED_SEGMENTS]);
};

/** Landings live at the root, so a slug matching an app route would shadow it. */
export const findRegistryErrors = (
  entries: LandingRegistryEntry[] = LANDINGS,
): string[] => {
  const errors: string[] = [];
  const reserved = getAppRootSegments();
  const seen = new Set<string>();

  entries.forEach(({slug}) => {
    if (!SLUG_PATTERN.test(slug))
      errors.push(
        `"${slug}" is not a valid slug (lowercase letters, digits and dashes).`,
      );

    if (seen.has(slug)) errors.push(`"${slug}" is registered more than once.`);
    seen.add(slug);

    if (reserved.has(slug))
      errors.push(`"${slug}" collides with the existing app route /${slug}.`);
  });

  return errors;
};

const registryErrors = findRegistryErrors();

if (registryErrors.length > 0) {
  const message = `Invalid landings registry:\n- ${registryErrors.join('\n- ')}`;

  // Fail loudly while developing, where the mistake is cheap to fix. In a
  // deployed environment a bad slug must not take the whole app down: the
  // colliding landing simply loses to the app route it shadows.
  if (__IS_DEVELOPMENT__) throw new Error(message);
  // eslint-disable-next-line no-console
  else console.error(message);
}
